import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiMail, FiCalendar, FiEye, FiEyeOff } = FiIcons;

const UserManagement = () => {
  const { users, addUser, deleteUser, updateUser, currentUser } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (editingUser) {
      const result = updateUser(editingUser.id, formData);
      if (result.success) {
        setEditingUser(null);
        setFormData({ username: '', email: '', password: '', role: 'admin' });
      } else {
        setError(result.error);
      }
    } else {
      const result = addUser(formData);
      if (result.success) {
        setShowAddForm(false);
        setFormData({ username: '', email: '', password: '', role: 'admin' });
      } else {
        setError(result.error);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowAddForm(true);
  };

  const handleDelete = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const result = deleteUser(userId);
      if (!result.success) {
        setError(result.error);
      }
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setFormData({ username: '', email: '', password: '', role: 'admin' });
    setError('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">User Management</h2>
          <p className="text-warm-brown/70">Manage admin users and their permissions</p>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
          Add User
        </button>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit User Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-playfair text-warm-brown">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>
            <button
              onClick={resetForm}
              className="text-warm-brown/70 hover:text-warm-brown transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  required
                />
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-warm-brown font-medium mb-2">
                  Password {editingUser && '(leave blank to keep current)'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-4 py-2 pr-12 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                    required={!editingUser}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <SafeIcon 
                      icon={showPassword ? FiEyeOff : FiEye} 
                      className="text-warm-brown/50 hover:text-warm-brown transition-colors"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-warm-brown text-cream py-2 rounded-lg hover:bg-warm-brown/90 transition-colors"
              >
                {editingUser ? 'Update User' : 'Add User'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-2 rounded-lg hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-warm-brown/10">
          <h3 className="text-lg font-playfair text-warm-brown">Current Users</h3>
        </div>

        <div className="divide-y divide-warm-brown/10">
          {users.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="px-6 py-4 flex items-center justify-between hover:bg-cream/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="bg-cream p-3 rounded-full">
                  <SafeIcon icon={FiUser} className="text-warm-brown text-lg" />
                </div>
                <div>
                  <h4 className="font-medium text-warm-brown">
                    {user.username}
                    {user.id === currentUser?.id && (
                      <span className="ml-2 text-xs bg-gold-accent text-white px-2 py-1 rounded-full">
                        Current User
                      </span>
                    )}
                  </h4>
                  <div className="flex items-center gap-4 text-sm text-warm-brown/70">
                    <div className="flex items-center gap-1">
                      <SafeIcon icon={FiMail} className="text-xs" />
                      {user.email}
                    </div>
                    <div className="flex items-center gap-1">
                      <SafeIcon icon={FiCalendar} className="text-xs" />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  user.role === 'admin' 
                    ? 'bg-warm-brown text-cream' 
                    : 'bg-gold-accent text-white'
                }`}>
                  {user.role}
                </span>
                
                <button
                  onClick={() => handleEdit(user)}
                  className="p-2 text-warm-brown/70 hover:text-warm-brown hover:bg-cream rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiEdit2} className="text-sm" />
                </button>
                
                {user.id !== currentUser?.id && (
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;